import { VIEWPORT_HEIGHT, VIEWPORT_WIDTH } from '../../../../domain/Constants';
import type { ArrowDirection } from '../../components/atomic/buttons/arrows/Arrows';
import {
  getDeliveryPopupSize,
} from '../../components/atomic/dialog/delivery_popups/deliveryPopupLayout';
import type { MapCamera } from '../MapScreenModelScreen';
import { MAP_SCALE } from '../lands/landPositions';
import type { DeliveryPopupPlacement } from '../delivery_popups/deliveryPopupPositions';
import type { DeliveryHintDirection } from './deliveryHintPositions';

export type OffscreenHintCounts = Record<ArrowDirection, number>;

export type OffscreenPopupRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export const EMPTY_OFFSCREEN_HINT_COUNTS: OffscreenHintCounts = {
  up: 0,
  down: 0,
  left: 0,
  right: 0,
};

/** Map arrow direction ↔ Figma deliveries_count position key. */
export const ARROW_TO_HINT_DIRECTION: Record<
  ArrowDirection,
  DeliveryHintDirection
> = {
  up: 'top',
  down: 'down',
  left: 'left',
  right: 'right',
};

/**
 * Screen-space AABB of a world-placed popup under map camera + scale.
 * Matches mapWorldTransform after App stretch is inverted (camera in CSS px).
 */
export function popupScreenRect(
  rect: OffscreenPopupRect,
  camera: MapCamera,
  scale: number = MAP_SCALE,
): { left: number; top: number; right: number; bottom: number } {
  const left = rect.left * scale - camera.x;
  const top = rect.top * scale - camera.y;
  return {
    left,
    top,
    right: left + rect.width * scale,
    bottom: top + rect.height * scale,
  };
}

type ScreenRect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

function rectsIntersect(a: ScreenRect, b: ScreenRect): boolean {
  return (
    a.left < b.right &&
    a.right > b.left &&
    a.top < b.bottom &&
    a.bottom > b.top
  );
}

export function isPopupInViewport(
  rect: OffscreenPopupRect,
  camera: MapCamera,
  scale: number = MAP_SCALE,
  viewportWidth: number = VIEWPORT_WIDTH,
  viewportHeight: number = VIEWPORT_HEIGHT,
): boolean {
  const screen = popupScreenRect(rect, camera, scale);
  return rectsIntersect(screen, {
    left: 0,
    top: 0,
    right: viewportWidth,
    bottom: viewportHeight,
  });
}

/**
 * Same as v1 `primaryPanForOffscreenMarker`: one cardinal direction per
 * off-screen popup. On a diagonal, pick the axis with the larger gap;
 * ties prefer horizontal.
 */
export function primaryPanForOffscreenPopup(
  viewport: ScreenRect,
  popup: ScreenRect,
): ArrowDirection | null {
  if (rectsIntersect(viewport, popup)) {
    return null;
  }

  let hDir: 'left' | 'right' | null = null;
  let vDir: 'up' | 'down' | null = null;
  let gapX = 0;
  let gapY = 0;

  if (popup.right <= viewport.left) {
    hDir = 'left';
    gapX = viewport.left - popup.right;
  } else if (popup.left >= viewport.right) {
    hDir = 'right';
    gapX = popup.left - viewport.right;
  }

  if (popup.bottom <= viewport.top) {
    vDir = 'up';
    gapY = viewport.top - popup.bottom;
  } else if (popup.top >= viewport.bottom) {
    vDir = 'down';
    gapY = popup.top - viewport.bottom;
  }

  if (hDir != null && vDir != null) {
    return gapX >= gapY ? hDir : vDir;
  }
  if (hDir != null) {
    return hDir;
  }
  if (vDir != null) {
    return vDir;
  }

  const vcx = (viewport.left + viewport.right) / 2;
  const vcy = (viewport.top + viewport.bottom) / 2;
  const mcx = (popup.left + popup.right) / 2;
  const mcy = (popup.top + popup.bottom) / 2;
  const dx = mcx - vcx;
  const dy = mcy - vcy;
  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx < 0 ? 'left' : 'right';
  }
  return dy < 0 ? 'up' : 'down';
}

/**
 * For each off-screen popup, increments exactly one arrow direction
 * (v1 primary-pan behaviour).
 */
export function countOffscreenDeliveryHints(
  rects: readonly OffscreenPopupRect[],
  camera: MapCamera,
  scale: number = MAP_SCALE,
  viewportWidth: number = VIEWPORT_WIDTH,
  viewportHeight: number = VIEWPORT_HEIGHT,
): OffscreenHintCounts {
  const counts: OffscreenHintCounts = { ...EMPTY_OFFSCREEN_HINT_COUNTS };
  const viewport: ScreenRect = {
    left: 0,
    top: 0,
    right: viewportWidth,
    bottom: viewportHeight,
  };

  for (const rect of rects) {
    const screen = popupScreenRect(rect, camera, scale);
    const dir = primaryPanForOffscreenPopup(viewport, screen);
    if (dir != null) {
      counts[dir] += 1;
    }
  }

  return counts;
}

export function placementToOffscreenRect(
  placement: Pick<DeliveryPopupPlacement, 'left' | 'top' | 'tail'>,
): OffscreenPopupRect {
  const size = getDeliveryPopupSize(placement.tail ?? 'down');
  return {
    left: placement.left,
    top: placement.top,
    width: size.width,
    height: size.height,
  };
}
